import { QuantityAttributeSelectionTable } from "@fero/components";
import { useTradeSalesStore } from "@fero/stores";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import type { FormikProps } from "formik";
import { useMemo } from "react";
import { useTradeSaleHelpers } from "../hooks";
import type { SelectedProductsFormValues } from "../new-organisation-purchase-request-setup-page";

interface AttributeSelectionCardProps {
  form: FormikProps<SelectedProductsFormValues>;
}

export const AttributeSelectionCard = ({
  form,
}: AttributeSelectionCardProps) => {
  const { storageLocationId, nominatedPurchaser } = useTradeSaleHelpers();
  const { reservedId, releasedId } = useTradeSalesStore();

  const onAddAttributeQuantity = (
    productId: string,
    attributeValue: string,
    quantity: number,
    batchNumber?: string,
    bestBefore?: string,
    rotationNumber?: string,
  ) => {
    form.setFieldValue(
      "selectedProducts",
      form.values.selectedProducts.map((product) => {
        if (product.productId === productId) {
          return {
            ...product,
            attributeSelection: {
              ...product.attributeSelection,
              [attributeValue]: {
                bestBefore: bestBefore ? new Date(bestBefore) : undefined,
                batchNumber,
                quantity,
                rotationNumber,
              },
            },
          };
        }
        return product;
      }),
    );
  };

  const onChangeAttributeQuantity = (
    productId: string,
    attributeValue: string,
    quantity: number,
  ) => {
    form.setFieldValue(
      "selectedProducts",
      form.values.selectedProducts.map((product) => {
        if (product.productId === productId) {
          return {
            ...product,
            attributeSelection: {
              ...product.attributeSelection,
              [attributeValue]: {
                ...product.attributeSelection[attributeValue],
                quantity,
              },
            },
          };
        }
        return product;
      }),
    );
  };

  const onDeleteAttributeQuantity = (
    productId: string,
    attributeValue: string,
  ) => {
    form.setFieldValue(
      "selectedProducts",
      form.values.selectedProducts.map((product) => {
        if (product.productId === productId) {
          const attributeSelection = { ...product.attributeSelection };
          delete attributeSelection[attributeValue];
          return {
            ...product,
            attributeSelection,
          };
        }
        return product;
      }),
    );
  };

  const productsWithSelectedQuantities = useMemo(
    () =>
      form.values.selectedProducts.map((product) => ({
        productId: product.productId,
        productName: product.summary.value.name,
        unitType: product.summary.value.unitType,
        quantityRequired: product.quantity + product.freeUnits,
        attributeSelection: product.attributeSelection || {},
      })),
    [form.values.selectedProducts],
  );

  const attributeConfig = {
    attributeKey: "batch_number" as const,
    displayName: "Batch",
    additionalInfoKey: "best_before" as const,
    additionalInfoLabel: "Best Before",
  };

  return (
    <BaseWizardCard
      title={"Batch Selection"}
      subtitle={"Please review the batches selected for this order."}
      inputElement={
        <QuantityAttributeSelectionTable
          onAttributeQuantityAdd={onAddAttributeQuantity}
          onAttributeQuantityChange={onChangeAttributeQuantity}
          onAttributeQuantityRemove={onDeleteAttributeQuantity}
          attributeConfig={attributeConfig}
          productsWithSelectedQuantities={productsWithSelectedQuantities}
          organisationId={nominatedPurchaser.organisationId}
          storageLocationId={storageLocationId}
          holdId={reservedId}
          releaseId={releasedId}
        />
      }
    />
  );
};
